/**
 * Build-time feature visibility flags.
 *
 * CONCIERGE_VISIBLE — the AI Concierge (settings screen + client chat bubble).
 * Turned OFF 2026-08-03 so the marketing release can go out on its own; the
 * code ships but nothing surfaces. Flip to true to bring it back — no other
 * change needed. The BE has its own independent kill-switch
 * (CONCIERGE_ENABLED=false) plus per-salon enablement, so this is the outermost
 * of three switches.
 */
// AMENDED 2026-08-05: visible on dev/staging ONLY, rather than a single boolean.
// Beta shares prod's database and its real salon accounts, so `= true` would
// have launched an unfinished AI receptionist to real salons the moment anyone
// merged staging into beta. This lets it be tested end to end with no such risk.
//
// AMENDED 2026-08-06: staging removed too — the concierge is PARKED and the SE
// booking-link update needs to go out on its own, so nothing concierge-shaped
// should be on screen while that release is checked. 'local' and 'dev' keep it
// reachable for development.
//
// TO RESUME: put 'stag' back in the list. TO LAUNCH: return true
// unconditionally, or add 'beta' / 'prod'.
//
// Still the outermost of THREE switches — the BE has its own kill-switch
// (CONCIERGE_ENABLED=false) and every salon is off until it opts in — so making
// this visible surfaces the settings screen; it doesn't turn anything on.
// RESUMED 2026-09-09: 'stag' back in, at Charlie's call. Beta and prod stay out
// deliberately — beta shares prod's database and its real salon accounts, so
// adding either would put an untested AI receptionist in front of real salons
// the moment someone merged. This surfaces the settings screen on staging; it
// still turns nothing on by itself.
const CONCIERGE_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const CONCIERGE_VISIBLE =
    CONCIERGE_VISIBLE_ENVS.indexOf(process.env.REACT_APP_ENV) !== -1;

/**
 * TAXES_VISIBLE — the tax-rates feature (Taxes catalogue tab, /taxes page,
 * TaxPicker on service/product/voucher editors). Pulled from the 2026-08-03
 * release (Charlie) and hidden everywhere since.
 *
 * AMENDED 2026-09-15: visible on staging ONLY, so it can be tested, rather than
 * flipped to a flat `true`. Beta shares prod's database and its real salon
 * accounts, so `= true` would put tax configuration in front of real salons the
 * moment anyone merged staging into beta. Env-scoping makes that impossible by
 * construction instead of by remembering. Same pattern as CONCIERGE_VISIBLE
 * above, and for the same reason.
 *
 * NOTE this is the ONLY switch. Unlike the concierge, the backend has no
 * kill-switch and no per-salon opt-in — lib/module/v2/tax is already on
 * staging, beta AND master, so the API is reachable in every environment
 * regardless of this flag. This only controls whether anything is on screen.
 *
 * TO LAUNCH: return true unconditionally, or add 'beta' / 'prod'.
 */
const TAXES_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const TAXES_VISIBLE =
    TAXES_VISIBLE_ENVS.indexOf(process.env.REACT_APP_ENV) !== -1;

/**
 * ONBOARDING_DEMO_VISIBLE — Settings > Onboarding, the page that restarts or
 * replays the first-login setup so it can be demoed on a salon that already
 * exists. Never on a customer-facing build: reset would wipe real progress.
 *
 * Bailey's welcome and "Set up with Bailey" moved to SETUP_V2_VISIBLE below
 * (launched 2026-10-14); this flag now gates only the demo and reset tools.
 *
 * AMENDED 2026-09-26 (setup audit): an ALLOWLIST, like the flags around it.
 * It used to be a blocklist of prod/beta/rush, so any build without
 * REACT_APP_ENV (a plain `npm run build`, a typo'd env name) switched the
 * whole unfinished setup on. Now only the named dev environments see it.
 * TO LAUNCH: add 'beta' / 'prod', or return true.
 */
const ONBOARDING_DEMO_VISIBLE_ENVS = ['local', 'dev', 'stag'];
export const ONBOARDING_DEMO_VISIBLE =
    ONBOARDING_DEMO_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * SETUP_V2_VISIBLE — Bailey's welcome and "Set up with Bailey", the guided
 * setup a new signup lands in (?setup=1). Launched to beta and prod
 * 2026-10-14. Established salons never see it (server setupEligible), and the
 * demo/reset pages stay behind ONBOARDING_DEMO_VISIBLE.
 */
const SETUP_V2_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const SETUP_V2_VISIBLE =
    SETUP_V2_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * SAVINGS_CALCULATOR_VISIBLE — Settings > Savings calculator, the stand tool
 * for exhibitions and demos: what a salon's software, admin time and
 * new-client commission cost them against Beu, with a sign-up QR code and
 * details kept on the device. A sales tool, never on a customer build.
 */
const SAVINGS_CALCULATOR_VISIBLE_ENVS = ['local', 'dev', 'stag'];
export const SAVINGS_CALCULATOR_VISIBLE =
    SAVINGS_CALCULATOR_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * AGENT_VISIBLE — the Beu agent (the salon/SE assistant thread).
 *
 * Staging and below only, for the same reason as the two above: beta shares
 * prod's database and its real salon accounts, so a flat `true` would put an
 * unfinished assistant in front of real salons the moment someone merged
 * staging into beta. Env-scoping makes that impossible by construction rather
 * than by remembering.
 *
 * Outermost of THREE switches — the BE has AGENT_ENABLED=false and every
 * principal is off until they opt in — so making this visible surfaces the
 * screen; it turns nothing on.
 *
 * TO LAUNCH: return true unconditionally, or add 'beta' / 'prod'.
 */
const AGENT_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const AGENT_VISIBLE =
    AGENT_VISIBLE_ENVS.indexOf(process.env.REACT_APP_ENV) !== -1;

/**
 * PERMISSIONS_VISIBLE — roles & permissions (Team > Permissions, Access on a
 * team member's profile) and the UI gates that read capabilities.
 *
 * Off: every screen gates exactly as before (owner / employment checks), and
 * the Permissions tab is hidden. The BACKEND enforces capabilities whatever
 * this says — its defaults reproduce today's access, so a salon nobody has
 * configured sees no change either way (beu-node-v2 docs/permissions.md).
 *
 * Staging and below only: beta shares prod's database.
 * TO LAUNCH: return true unconditionally, or add 'beta' / 'prod'.
 */
const PERMISSIONS_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const PERMISSIONS_VISIBLE =
    PERMISSIONS_VISIBLE_ENVS.indexOf(process.env.REACT_APP_ENV) !== -1;

/**
 * REFUNDS_VISIBLE: the "Refund" action on a completed sale (transaction
 * panel + booking panel), beu-node-v2 docs/refunds.md. Refund money moves on
 * real cards, so it is staging and below only until the engine has soaked:
 * beta shares prod's database. Badges and the refund lines on finance pages
 * are not behind this: they only read fields and hide when there are none.
 *
 * The backend enforces who can refund whatever this says.
 * TO LAUNCH: return true unconditionally, or add 'beta' / 'prod'.
 */
const REFUNDS_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const REFUNDS_VISIBLE =
    REFUNDS_VISIBLE_ENVS.indexOf(process.env.REACT_APP_ENV) !== -1;


/**
 * EMAIL_TEMPLATES_V2_VISIBLE — the new email template library, brand panel
 * and email-native sections in the campaign email creator (27 Sept 2026).
 *
 * Staging and below only. The new emails are complete HTML built in the
 * browser and sent to the API as `emailHtml` on campaign create/update. The
 * API now stores and sends it (campaignFacade create/update, and the send
 * worker sends the stored HTML), so the blocker named here before is gone;
 * what's left is the launch decision. Emails already made with the classic
 * builder are untouched either way.
 *
 * TO LAUNCH: add 'beta' / 'prod', or return true.
 */
const EMAIL_TEMPLATES_V2_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const EMAIL_TEMPLATES_V2_VISIBLE =
    EMAIL_TEMPLATES_V2_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * SOCIAL_VISIBLE — Marketing › Social: Instagram and TikTok posts made from
 * the salon's own diary, reviews and photos, a social calendar and the
 * diary's Today chip (27 Sept 2026, phase 1).
 *
 * Staging and below only. Posts are stored on the server (social/posts);
 * posting to Instagram/TikTok is by hand (save, copy caption, "I've posted
 * it") until Meta/TikTok approval. To launch, add 'beta' / 'prod'.
 */
const SOCIAL_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const SOCIAL_VISIBLE =
    SOCIAL_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * CLIENT_PHOTOS_VISIBLE — the Photos tab on a client and the photo strip in
 * the booking panel (built April 2026, hidden 27 May because photo consent
 * lives on SalonClient and that store was empty before the client-flow
 * migration). The migration has landed, so staging and below show it again.
 *
 * TO LAUNCH: prod's photo endpoints need the salon-scoping fix (sec D6,
 * beu-node-v2 7b158c6e, staging only today) before 'beta' / 'prod' go here.
 */
const CLIENT_PHOTOS_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const CLIENT_PHOTOS_VISIBLE =
    CLIENT_PHOTOS_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * SOCIAL_CLIENT_PHOTOS_VISIBLE — the "Client photos" source in the social
 * post studio's photo picker: search a client, see the photos on their
 * record, and only use the ones they've said yes to sharing (28 Sept 2026).
 *
 * Needs both Social and client photos. Sharing consent is kept as the
 * photo's `social` tag for now (docs/SOCIAL_BACKEND.md).
 */
export const SOCIAL_CLIENT_PHOTOS_VISIBLE = SOCIAL_VISIBLE && CLIENT_PHOTOS_VISIBLE;

/**
 * MARKETING_HOME_VISIBLE — Marketing › Home (the command centre: Bailey's
 * plans, what's going out, results, every section) and Marketing › Calendar
 * (posts, emails, texts and dates to know together), 28 Sept 2026.
 *
 * Staging and below only. Off: Marketing opens on Campaigns as before.
 * TO LAUNCH: add 'beta' / 'prod' once Social is launched (the home leans on
 * Bailey's posts and the social store).
 */
const MARKETING_HOME_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const MARKETING_HOME_VISIBLE =
    MARKETING_HOME_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * Campaigns with Bailey (30 Sept 2026): multi-channel campaigns on the
 * Campaigns tab (posts, Stories, texts and emails over a day to six weeks),
 * seasonal and quiet-spell campaigns from Bailey, and campaign bands on the
 * calendar. Off: the Campaigns tab is exactly as before (Email and SMS).
 * Rides with the marketing home, which the builder and Bailey's plans share.
 * TO LAUNCH: with MARKETING_HOME_VISIBLE.
 */
export const CAMPAIGNS_VISIBLE = MARKETING_HOME_VISIBLE;

/**
 * PERFORMANCE_V2_VISIBLE — Marketing › Performance rebuilt around everything
 * marketing does (30 Sept 2026): an Overview of what it brought in, and tabs
 * for Campaigns, Social, Journeys and reminders, Loyalty and vouchers, and
 * the front desk. Off: the Performance page exactly as before
 * (LegacyPerformance.js). Rides with the marketing home, whose tiles read
 * the same numbers.
 * TO LAUNCH: with MARKETING_HOME_VISIBLE, then delete LegacyPerformance.js
 * and the .perf- styles in Campaigns.css.
 */
export const PERFORMANCE_V2_VISIBLE = MARKETING_HOME_VISIBLE;

/**
 * PRICING_V2_VISIBLE — the October 2026 pricing (Charlie): Settings › Plan &
 * usage, the Beu + Bailey upgrade, Bailey's free-question counter, the
 * receptionist allowance and the light balance / receptionist alerts.
 *
 * Staging and below only: beta shares prod's database, and the upgrade
 * changes what a real salon pays. The pounds wording on the marketing
 * wallet is NOT behind this: it shows the same balance (credits × 2p).
 * TO LAUNCH: add 'beta' / 'prod', or return true.
 */
const PRICING_V2_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const PRICING_V2_VISIBLE =
    process.env.NODE_ENV === 'test' ||
    PRICING_V2_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * STOCK_EDIT_VISIBLE: set and adjust stock in the product editor (starting
 * stock on a new product; − / + with a reason on an existing one). Waits on
 * the backend: product add must accept `quantity`, and POST
 * product/stock-adjust must exist (see docs/STOCK_EDIT_BACKEND.md). Local only
 * until then, so nobody sees a stock box the server ignores.
 * TO LAUNCH: add 'dev', 'stag', then 'beta' / 'prod'.
 */
const STOCK_EDIT_VISIBLE_ENVS = ['local'];
export const STOCK_EDIT_VISIBLE =
    STOCK_EDIT_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * DISCOUNTS_VISIBLE: discount codes (Oct 2026). Marketing › Discounts, a
 * stylist's own codes and their choice on salon codes, "Have a discount
 * code?" when booking online, Add a code at checkout and the till, and live
 * codes in Social, email and texts. Plan:
 * https://claude.ai/artifact/G7bFTBin9WSyuZH1oDu1bV, server brief
 * docs/DISCOUNT_CODES_BACKEND.md.
 *
 * Staging and below only: codes change what clients pay, and beta shares
 * prod's database. Keep it here until the server checks and redeems codes
 * itself (discount/validate, promo on the booking calls).
 * TO LAUNCH: add 'beta' / 'prod' once the server side is live.
 */
// Beta on (shares prod's DB: real salons, real money) to soak before prod.
const DISCOUNTS_VISIBLE_ENVS = ['local', 'dev', 'stag', 'beta', 'prod'];
export const DISCOUNTS_VISIBLE =
    DISCOUNTS_VISIBLE_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

/**
 * DISCOUNTS_STAND_IN: until the discount/* endpoints exist, codes are kept on
 * the device (IndexedDB, which survives sign-out) and checked there, so every
 * flow can be tried end to end on staging. Never on beta or prod, even after
 * DISCOUNTS_VISIBLE launches: a live salon must never run on device data.
 */
// Its own list: turning DISCOUNTS_VISIBLE on for beta/prod must never turn this on.
const DISCOUNTS_STAND_IN_ENVS = ['local', 'dev', 'stag'];
export const DISCOUNTS_STAND_IN =
    process.env.NODE_ENV === 'test' || DISCOUNTS_STAND_IN_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

export default {
    STOCK_EDIT_VISIBLE,
    PRICING_V2_VISIBLE,
    CONCIERGE_VISIBLE, TAXES_VISIBLE, ONBOARDING_DEMO_VISIBLE, SETUP_V2_VISIBLE, SAVINGS_CALCULATOR_VISIBLE, AGENT_VISIBLE, PERMISSIONS_VISIBLE, REFUNDS_VISIBLE,
    EMAIL_TEMPLATES_V2_VISIBLE, SOCIAL_VISIBLE, CLIENT_PHOTOS_VISIBLE, SOCIAL_CLIENT_PHOTOS_VISIBLE, MARKETING_HOME_VISIBLE,
    CAMPAIGNS_VISIBLE, PERFORMANCE_V2_VISIBLE, DISCOUNTS_VISIBLE, DISCOUNTS_STAND_IN,
};
